import { useMemo, type FunctionComponent } from 'react';
import { IntlProvider } from 'react-intl';

import FabricAnalyticsListeners, {
  FabricChannel,
} from '@atlaskit/analytics-listeners';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import RemoteSwitcherPlugin from '@atlassian/remote-module-loader/plugin-switcher';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import AtlassianSwitcher from '@atlassian/switcher';
import { getAnalyticsClientMemberIdProxy } from '@trello/atlassian-analytics';
import { environment } from '@trello/config';
import { ErrorBoundary } from '@trello/error-boundaries';
import { currentLocale } from '@trello/locale';
import { Placement } from '@trello/post-office';
import { convertToPIIString, type PIIString } from '@trello/privacy';

export interface AtlassianAppSwitcherProps {
  aaId?: string;
  locale?: PIIString;
  onClose: () => void;
  highlightedJoinableItemHref?: string;
}

// oxlint-disable-next-line @trello/no-module-logic
const defaultLocale = convertToPIIString('en');
export const AtlassianAppSwitcher: FunctionComponent<
  AtlassianAppSwitcherProps
> = ({
  aaId,
  locale = defaultLocale,
  onClose,
  highlightedJoinableItemHref,
}) => {
  const recommendationsFeatureFlags = { isProductStoreInTrelloEnabled: true };

  const switcherProps = {
    product: 'trello',
    highlightedJoinableItemHref,
    onClose,
    recommendationsFeatureFlags,
    discoverySection: (
      <Placement
        placementId="app-switcher-discovery-section"
        shouldIgnoreFabricAnalyticsListeners
      />
    ),
  };

  const analyticsContextData = {
    navigationCtx: {
      attributes: {
        aaId,
        isAaMastered: true,
      },
    },
  };

  const remoteSwitcherPlugin = useMemo(() => {
    return new RemoteSwitcherPlugin({
      env: environment === 'prod' ? 'production' : 'staging',
      variant: 'latest',
    });
  }, []);

  return (
    <IntlProvider
      key={currentLocale}
      data-test-language={currentLocale}
      locale={currentLocale}
    >
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-web-eng',
          feature: 'Atlassian Switcher',
        }}
        sendCrashEvent={false}
      >
        <FabricAnalyticsListeners
          // @ts-expect-error
          client={getAnalyticsClientMemberIdProxy()}
          excludedChannels={[
            FabricChannel.atlaskit,
            FabricChannel.elements,
            FabricChannel.editor,
            FabricChannel.media,
          ]}
        >
          <AnalyticsContext data={analyticsContextData}>
            <RemoteComponent<AtlassianAppSwitcherProps>
              remotePlugin={remoteSwitcherPlugin}
              ErrorFallback={<AtlassianSwitcher {...switcherProps} />}
              {...switcherProps}
            />
          </AnalyticsContext>
        </FabricAnalyticsListeners>
      </ErrorBoundary>
    </IntlProvider>
  );
};
